import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Image } from "@/components/ui/image";
import { Menu, X, Download, ChevronDown, Building2, Store, FileSearch, Fingerprint, Brain, Cpu, Radar, MapPin, ShieldCheck, Scale, Package, AlertTriangle, Server, Users, Truck, Home } from "lucide-react";
import DownloadAppModal from "@/components/DownloadAppModal";
import ThemeToggle from "@/components/ThemeToggle";
import SoundToggle from "@/components/SoundToggle";
import LanguageSelector from "@/components/LanguageSelector";
import UserMenu from "@/components/UserMenu";
import NavSearchOverlay from "@/components/landing/NavSearchOverlay";
import { useAuth } from "@/lib/AuthContext";
import { CARRIERS } from "@/lib/carriers";
import { getCarrierSlug } from "@/lib/carrierSeo";
import { useDomainBranding } from "@/hooks/useDomainBranding";

const LOGO_URL = "https://media.base44.com/images/public/6aa6211001d513cafb0a3a5c/8b0969465_BHGC.png";

const MAIN_CARRIER_IDS = ["DHL", "FedEx", "Estafeta", "Paquetexpress", "UPS", "J&T"];
const carrierChildren = [
  ...MAIN_CARRIER_IDS.map(id => {
    const c = CARRIERS.find(x => x.id === id);
    return { label: c?.name || id, to: `/paqueterias/${getCarrierSlug(id)}`, main: true };
  }),
  ...CARRIERS.filter(c => !MAIN_CARRIER_IDS.includes(c.id)).map(c => ({
    label: c.name, to: `/paqueterias/${getCarrierSlug(c.id)}`, main: false,
  })),
  { label: "Ver todas las paqueterías", to: "/comparar-paqueterias", all: true },
];

const resourceChildren = [
  { label: "Guías y recursos", to: "/recursos" },
  { label: "Comparar paqueterías", to: "/comparar-paqueterias" },
  { label: "Sucursales de paqueterías", to: "/sucursales" },
  { label: "Agregadores", to: "/agregadores" },
  { label: "Zonas postales", to: "/zonas-postales" },
  { label: "Envíos internacionales", to: "/envios-internacionales" },
  { label: "Cotización en tiempo real", to: "/cotizacion-en-tiempo-real" },
  { label: "Rastreo centralizado", to: "/rastreo-centralizado" },
  { label: "Mapa logístico", to: "/mapa-logistico" },
  { label: "Recolección agendable", to: "/recoleccion-agendable" },
  { label: "Saldo prepago", to: "/saldo-prepago" },
  { label: "Cotizar envío", to: "/cotizar" },
  { label: "Tienda online", to: "/tienda" },
  { label: "Cobertura por código postal", to: "/cobertura" },
  { label: "Calculadora volumétrica", to: "/calculadora-volumetrica" },
  { label: "Calculadora de ahorro", to: "/calculadora-ahorro" },
  { label: "Estudio de ahorro", to: "/estudio-de-ahorro" },
  { label: "Cotizador embebible", to: "/cotizador-embebible" },
  { label: "Rastreador embebible", to: "/rastreador-embebible" },
  { label: "Plugin WooCommerce", to: "/plugin-woocommerce" },
  { label: "Integración Shopify", to: "/integracion-shopify" },
  { label: "Integración TiendaNube", to: "/integracion-tiendanube" },
  { label: "API REST de envíos", to: "/api-rest" },
  { label: "Centro de Ayuda", to: "/ayuda" },
];

// Submenú "Blog" — artículos destacados + enlace al índice del blog.
// Los slugs coinciden con src/lib/blogArticles.js (y blogArticlesExtra.js).
const blogChildren = [
  { label: "Ver todo el blog", to: "/blog", all: true },
  { label: "El agregador de agregadores", to: "/blog/el-agregador-de-agregadores" },
  { label: "SEO logístico en México", to: "/blog/seo-logistico-mexico" },
  { label: "Envia.com vs BHGC Nexus", to: "/blog/envia-com-vs-bhgc-nexus" },
  { label: "Marca blanca logística", to: "/blog/marca-blanca-logistica-white-label" },
  { label: "Franquicia logística SaaS", to: "/blog/franquicia-logistica-saas" },
  { label: "Hub logístico SaaS México", to: "/blog/hub-logistico-saas-mexico" },
  { label: "Plataformas de envío en México", to: "/blog/plataformas-de-envio-mexico" },
  { label: "IA, Copilot y Gemini en logística", to: "/blog/ia-copilot-gemini-logistica" },
  { label: "Widgets embebibles de envío", to: "/blog/widgets-embebibles-de-envio" },
  { label: "Directorio de sucursales de paqueterías", to: "/blog/directorio-de-sucursales-de-paqueterias" },
  { label: "Cotizador de envíos embebible", to: "/blog/cotizador-de-envios-embebible" },
  { label: "Rastreador de paquetes embebible", to: "/blog/rastreador-de-paquetes-embebible" },
];

// Submenú "Preguntas" — enlaces directos a cada pregunta del FAQ del landing.
// Los hash (#faq-<slug>) coinciden con los id definidos en LANDING_FAQS (src/pages/Landing.jsx).
const preguntasChildren = [
  { label: "¿Cómo funciona?", to: "/#faq-como-funciona" },
  { label: "¿Con qué paqueterías trabajan?", to: "/#faq-paqueterias" },
  { label: "¿Tiene algún costo?", to: "/#faq-costo" },
  { label: "¿Las guías incluyen protección?", to: "/#faq-proteccion" },
  { label: "¿Puedo facturar?", to: "/#faq-facturar" },
  { label: "¿Cómo rastreo mis envíos?", to: "/#faq-rastreo" },
  { label: "¿Qué pasa si se retrasa?", to: "/#faq-retraso" },
  { label: "¿Puedo conectar mi tienda?", to: "/#faq-tienda" },
];

// Submenú "Integraciones" — estilo grid con iconos y descripción (distinto a los
// submenús ya usados: Recursos=lista simple, Paqueterías=wide 3 columnas,
// Corporativo=rich con header). Agrupa IA + APIs + servicio de protección de envíos.
const integracionesChildren = [
  { label: "Cotizador con IA", to: "/cotizador-con-ia", icon: Brain, desc: "Cotiza en lenguaje natural y automatiza sin clic" },
  { label: "IA Integradas", to: "/ia-integradas", icon: Cpu, desc: "Gemini, Mistral, Llama, Groq y cadena de respaldo" },
  { label: "Rastreadores API + IA", to: "/rastreadores-integrados-api", icon: Radar, desc: "Rastreo unificado de 30+ carriers por API con IA" },
  { label: "Conexiones API Maps", to: "/conexiones-api-maps", icon: MapPin, desc: "Google Maps y OpenStreetMap integrados" },
  { label: "Servicio de protección de envíos", to: "/seguro-adicional-cudo", icon: ShieldCheck, desc: "Protege el valor declarado de tu envío con un click" },
];

const corporativoChildren = [
  { label: "Planes SaaS", to: "/planes-saas", icon: Store, desc: "Suscripción mensual, paga al enviar" },
  { label: "Programa de Afiliados", to: "/afiliados", icon: Users, desc: "Gana saldo por cada cliente que refieras" },
  { label: "Modelo Franquicia", to: "/franquicia", icon: Building2, desc: "White-label con billetera maestra" },
  { label: "Auditoría de Valor", to: "/auditoria-valor", icon: FileSearch, desc: "Transparencia financiera y contable" },
  { label: "Autoría Verificable", to: "/autor", icon: Fingerprint, desc: "Huella criptográfica SHA-256 del autor" },
];

// Mega-menú "Legal y Política" — agrupa las ~46 páginas de políticas en 7
// categorías con icono. Se renderiza como panel ancho de 4 columnas en
// desktop (mega) y como acordeón scrollable en móvil. Refuerza el modelo de
// agregador de agregadores: intermediario tecnológico, no transportista final.
const legalGroups = [
  {
    title: "Corporativa",
    icon: Building2,
    links: [
      { t: "Quiénes Somos", to: "/info/quienes-somos" },
      { t: "Misión, Visión y Valores", to: "/info/mision-vision-valores" },
      { t: "Cobertura Nacional e Internacional", to: "/info/cobertura" },
      { t: "Aliados Logísticos", to: "/info/aliados-logisticos" },
      { t: "Bolsa de Trabajo", to: "/info/bolsa-de-trabajo" },
      { t: "Prensa y Medios", to: "/info/prensa" },
      { t: "Contacto Corporativo", to: "/info/contacto-corporativo" },
    ],
  },
  {
    title: "Legal",
    icon: Scale,
    links: [
      { t: "Términos y Condiciones", to: "/terminos" },
      { t: "Aviso de Privacidad", to: "/privacidad" },
      { t: "Política de Cookies", to: "/legal/cookies" },
      { t: "Tratamiento de Datos", to: "/legal/tratamiento-datos" },
      { t: "Política Anti-Spam", to: "/legal/anti-spam" },
      { t: "Cumplimiento Normativo", to: "/legal/cumplimiento" },
      { t: "Propiedad Intelectual", to: "/legal/propiedad-intelectual" },
      { t: "Código de Ética y Conducta", to: "/legal/codigo-etica" },
    ],
  },
  {
    title: "Envíos",
    icon: Package,
    links: [
      { t: "Artículos Prohibidos y Restringidos", to: "/politicas-envio" },
      { t: "Empaque y Embalaje", to: "/envios/empaque-embalaje" },
      { t: "Mercancías Frágiles", to: "/envios/fragiles" },
      { t: "Mercancías de Alto Valor", to: "/envios/alto-valor" },
      { t: "Documentos Oficiales", to: "/envios/documentos-oficiales" },
      { t: "Mercancías Perecederas", to: "/envios/perecederos" },
      { t: "Baterías de Litio y Electrónicos", to: "/envios/baterias-litio" },
      { t: "Servicio de Protección de Envíos", to: "/envios/seguros" },
    ],
  },
  {
    title: "Reclamaciones",
    icon: AlertTriangle,
    links: [
      { t: "Limitación de Responsabilidad", to: "/reclamaciones/limitacion-responsabilidad" },
      { t: "Política de Indemnización", to: "/reclamaciones/indemnizacion" },
      { t: "Política de Reclamaciones", to: "/reclamaciones/reclamaciones" },
      { t: "Daños y Pérdidas", to: "/reclamaciones/danos-perdidas" },
      { t: "Devoluciones y Retornos", to: "/reclamaciones/devoluciones" },
      { t: "Resolución de Controversias", to: "/reclamaciones/controversias" },
      { t: "Exclusión de Garantías", to: "/reclamaciones/exclusion-garantias" },
    ],
  },
  {
    title: "Plataforma",
    icon: Server,
    links: [
      { t: "Condiciones de Uso", to: "/plataforma/condiciones-uso" },
      { t: "Condiciones API", to: "/plataforma/condiciones-api" },
      { t: "Términos para Integradores", to: "/plataforma/terminos-integradores" },
      { t: "Seguridad de la Información", to: "/plataforma/seguridad-informacion" },
      { t: "Política de Ciberseguridad", to: "/plataforma/ciberseguridad" },
      { t: "Disponibilidad (SLA)", to: "/plataforma/sla" },
      { t: "Política de Uso Aceptable", to: "/plataforma/uso-aceptable" },
    ],
  },
  {
    title: "Clientes",
    icon: Users,
    links: [
      { t: "Registro y Verificación", to: "/clientes/registro-verificacion" },
      { t: "Política KYC", to: "/clientes/kyc" },
      { t: "Política Antifraude", to: "/clientes/antifraude" },
      { t: "Política de Cancelación", to: "/clientes/cancelacion" },
      { t: "Política de Reembolsos", to: "/clientes/reembolsos" },
      { t: "Métodos de Pago", to: "/clientes/metodos-pago" },
      { t: "Facturación CFDI", to: "/clientes/facturacion-cfdi" },
    ],
  },
  {
    title: "Proveedores",
    icon: Truck,
    links: [
      { t: "Términos para Proveedores", to: "/proveedores/terminos" },
      { t: "Requisitos de Afiliación", to: "/proveedores/afiliacion" },
      { t: "Política de Calidad", to: "/proveedores/calidad" },
      { t: "Acuerdos de Nivel de Servicio", to: "/proveedores/ans" },
      { t: "Responsabilidad de Terceros", to: "/proveedores/responsabilidad-terceros" },
    ],
  },
];

const explorarChildren = [
  { label: "Cómo funciona", to: "/#como-funciona" },
  { label: "Beneficios", to: "/#beneficios" },
  { label: "Precios", to: "/#precios" },
];

const NAV_LINKS = [
  { label: "Explorar", children: explorarChildren },
  { label: "Paqueterías", children: carrierChildren, wide: true },
  { label: "Recursos", children: resourceChildren },
  { label: "Integraciones", to: "/#integraciones", children: integracionesChildren, grid: true },
  { label: "Blog", to: "/blog", children: blogChildren },
  { label: "Preguntas", to: "/#faq", children: preguntasChildren },
  { label: "Corporativo", to: "/corporativo", featured: corporativoChildren, children: legalGroups, mega: true },
];

export default function LandingNav() {
  const { user } = useAuth();
  const branding = useDomainBranding();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  // Destino del enlace "Integraciones": si el usuario está logueado y el menú
  // /integraciones del sidebar está activo (no oculto en el dispositivo actual),
  // lleva a la plataforma (/integraciones); si no, desplaza a la sección del
  // landing (#integraciones).
  const navigate = useNavigate();

  // Desplazamiento suave a la sección del landing. Si el usuario está logueado
  // la landing vive en /inicio (la ruta / redirige a /panel), así que los enlaces
  // #seccion no pueden usar Link a "/#seccion" porque dispararían esa redirección.
  // En su lugar: si la sección existe en la página actual, scrollIntoView; si no,
  // navegamos al destino correcto (/inicio para logueados, / para invitados) con
  // el hash y dejamos que el efecto de Landing haga el scroll al montar.
  const handleSectionClick = (e, target) => {
    if (!target || !target.startsWith("/#")) return; // ruta de plataforma: nav normal
    e.preventDefault();
    const hash = target.slice(2);
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`${user ? "/inicio" : "/"}#${hash}`);
    }
    setOpen(false);
  };
  // El enlace "Integraciones" siempre desliza a la sección del landing (incluso
  // logueado); el submenú grid enlaza a las páginas específicas.
  const resolvedTo = (l) => l.to;
  // El header transparente se apoya sobre el mapa satelital del hero del landing
  // (oscuro en cualquier tema): ahí el texto del nav usa blanco cuando NO se ha
  // hecho scroll. En el resto de páginas públicas el header está sobre el fondo
  // del tema, así que el texto debe ser ink-text para respetar el tema claro/oscuro.
  // Al hacer scroll el header gana fondo surface (tema) y el texto pasa a ink-text.
  const { pathname } = useLocation();
  const isLandingHero = pathname === "/" || pathname === "/inicio";
  const navColor = scrolled ? "text-ink-text" : (isLandingHero ? "text-white" : "text-ink-text");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`safe-top fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-ink-surface/80 bhgc-glass border-b border-ink-border" : "bg-transparent"}`}>
      <div className="max-w-[1500px] xl:max-w-[1600px] mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <Image src={branding?.logo_url || LOGO_URL} alt={branding?.brand_name ? `${branding.brand_name} ${branding.tagline || ""}`.trim() : "BHGC Nexus Logistics"} className="w-9 h-9 shrink-0 object-contain" fittingType="fit" />
          <div className="leading-tight shrink-0">
            <div className={`font-display font-bold text-sm tracking-wide ${navColor} whitespace-nowrap`}>{branding?.brand_name || "BHGC NEXUS"}</div>
            <div className="text-[0.58rem] uppercase tracking-widest text-cyan whitespace-nowrap">{branding?.tagline || "Logistics"}</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-3 lg:gap-5 whitespace-nowrap md:ml-4 lg:ml-6">
          <Link to={user ? "/inicio" : "/"} aria-label="Inicio" title="Inicio" className={`flex items-center ${navColor} hover:text-cyan transition-colors shrink-0`}>
            <Home className="w-4 h-4" />
          </Link>
          {NAV_LINKS.map(l => l.children ? (
            <div key={l.label} className="group relative shrink-0">
              <Link to={l.to} className={`flex items-center gap-1 text-sm font-medium ${navColor} hover:text-cyan transition-colors whitespace-nowrap`}>
                {l.label}
                <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />
              </Link>
              <div className={`invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 absolute top-full pt-2 transition-all duration-150 ${l.mega ? "right-0" : "left-1/2 -translate-x-1/2"}`}>
                {l.mega ? (
                  <div className="surface p-2.5 shadow-2xl w-[760px] max-w-[94vw]">
                    <Link to="/corporativo" className="block px-2 pt-1 pb-1.5 mb-1.5 border-b border-ink-border hover:bg-cyan/5 transition-colors">
                      <div className="text-[0.6rem] uppercase tracking-widest text-cyan font-bold">Corporativo</div>
                      <div className="text-[0.62rem] text-ink-muted mt-0.5 hover:text-cyan transition-colors">Modelos de negocio, transparencia, autoría y marco normativo integral</div>
                    </Link>
                    <div className="grid gap-2.5" style={{ gridTemplateColumns: "224px 1fr" }}>
                      <div className="border-r border-ink-border pr-3">
                        <div className="text-[0.6rem] uppercase tracking-widest text-cyan font-bold px-1 mb-1.5">Corporativo</div>
                        {l.featured && l.featured.map(c => {
                          const FIcon = c.icon;
                          return (
                            <Link key={c.label} to={c.to} className="flex items-start gap-2 px-2 py-1.5 hover:bg-cyan/5 transition-colors group/item min-w-0">
                              <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-cyan/40 text-cyan bg-cyan/5 group-hover/item:bg-cyan group-hover/item:text-ink transition-colors">
                                {FIcon && <FIcon className="w-4 h-4" />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-xs font-medium text-ink-text group-hover/item:text-cyan transition-colors truncate">{c.label}</span>
                                <span className="block text-[0.6rem] text-ink-muted leading-tight line-clamp-2 break-words">{c.desc}</span>
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                      <div>
                        <div className="text-[0.6rem] uppercase tracking-widest text-cyan font-bold px-1 mb-1.5">Legal y Política</div>
                        <div className="grid grid-cols-3 gap-x-2 gap-y-2">
                          {l.children.map(g => {
                            const GIcon = g.icon;
                            return (
                              <div key={g.title} className="min-w-0">
                                <div className="flex items-center gap-1.5 px-1 mb-1 border-b border-cyan/20 pb-0.5">
                                  <GIcon className="w-3 h-3 text-cyan shrink-0" />
                                  <span className="text-[0.58rem] uppercase tracking-wide font-display font-bold text-ink-text truncate">{g.title}</span>
                                </div>
                                {g.links.map(c => (
                                  <Link key={c.to} to={c.to} className="block px-1.5 py-0.5 text-[0.65rem] text-ink-muted hover:text-cyan hover:bg-cyan/5 transition-colors leading-snug truncate">{c.t}</Link>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : l.grid ? (
                  <div className="surface p-2 shadow-2xl w-[420px] max-w-[92vw]">
                    <Link to="/ia-integradas" className="block px-2 pt-1 pb-2 mb-1 border-b border-ink-border hover:bg-cyan/5 transition-colors">
                      <div className="text-[0.6rem] uppercase tracking-widest text-cyan font-bold">Integraciones</div>
                      <div className="text-[0.62rem] text-ink-muted mt-0.5 hover:text-cyan transition-colors">IA, APIs de rastreo, mapas y servicio de protección de envíos</div>
                    </Link>
                    <div className="grid grid-cols-2 gap-1">
                      {l.children.map(c => {
                        const Icon = c.icon;
                        return (
                          <Link key={c.label} to={c.to} className="flex items-start gap-2 px-2 py-2 hover:bg-cyan/5 transition-colors group/item min-w-0">
                            <span className="shrink-0 w-8 h-8 flex items-center justify-center border border-cyan/40 text-cyan bg-cyan/5 group-hover/item:bg-cyan group-hover/item:text-ink transition-colors">
                              {Icon && <Icon className="w-4 h-4" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-xs font-medium text-ink-text group-hover/item:text-cyan transition-colors truncate">{c.label}</span>
                              <span className="block text-[0.6rem] text-ink-muted leading-tight line-clamp-2 break-words">{c.desc}</span>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : l.rich ? (
                  <div className="surface p-2 shadow-2xl w-72 max-w-[92vw]">
                    <Link to="/corporativo" className="block px-2 pt-1 pb-2 mb-1 border-b border-ink-border hover:bg-cyan/5 transition-colors">
                      <div className="text-[0.6rem] uppercase tracking-widest text-cyan font-bold">Corporativo</div>
                      <div className="text-[0.62rem] text-ink-muted mt-0.5 hover:text-cyan transition-colors">Modelos de negocio, transparencia y autoría</div>
                    </Link>
                    {l.children.map(c => {
                      const Icon = c.icon;
                      return (
                        <Link key={c.label} to={c.to} className="flex items-start gap-2.5 px-2.5 py-2 hover:bg-cyan/5 transition-colors group/item">
                          <span className="shrink-0 w-8 h-8 flex items-center justify-center border border-cyan/40 text-cyan bg-cyan/5 group-hover/item:bg-cyan group-hover/item:text-ink transition-colors">
                            {Icon && <Icon className="w-4 h-4" />}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-xs font-medium text-ink-text group-hover/item:text-cyan transition-colors">{c.label}</span>
                            <span className="block text-[0.62rem] text-ink-muted leading-tight">{c.desc}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                ) : l.wide ? (
                  <div className="surface p-1.5 shadow-2xl w-[460px] max-w-[92vw]">
                    <div className="grid grid-cols-3 gap-0.5">
                      {l.children.filter(c => !c.all).map(c => (
                        <Link key={c.label} to={c.to} className={`block px-2.5 py-1.5 text-xs hover:text-cyan hover:bg-cyan/5 transition-colors ${c.main ? "text-cyan font-medium" : "text-ink-muted"}`}>
                          {c.label}
                        </Link>
                      ))}
                    </div>
                    {l.children.filter(c => c.all).map(c => (
                      <Link key={c.label} to={c.to} className="block mt-1.5 pt-2 border-t border-ink-border text-center text-xs text-cyan font-medium hover:bg-cyan/5 transition-colors">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="surface min-w-44 p-1.5 shadow-2xl">
                    {l.children.map(c => (
                      <Link key={c.label} to={c.to} onClick={(e) => handleSectionClick(e, c.to)} className={`block px-3 py-2 text-xs hover:text-cyan hover:bg-cyan/5 transition-colors ${c.all ? "border-t border-ink-border mt-1 pt-2.5 text-cyan font-medium" : "text-ink-muted"}`}>
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Link key={l.to} to={resolvedTo(l)} onClick={(e) => handleSectionClick(e, resolvedTo(l))} className={`text-sm font-medium ${navColor} hover:text-cyan transition-colors whitespace-nowrap shrink-0`}>{l.label}</Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 md:ml-3 shrink-0">
          <NavSearchOverlay iconClassName={navColor} />
          <LanguageSelector iconClassName={navColor} />
          <SoundToggle iconClassName={navColor} />
          <ThemeToggle iconClassName={navColor} />
          <button onClick={() => setDownloadOpen(true)} className="relative inline-flex items-center group transition-transform hover:scale-105 shrink-0">
            <span className="absolute inset-0 border border-cyan animate-ping opacity-40 pointer-events-none" style={{ animationDuration: "2.4s" }} />
            <span className="relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-cyan border border-cyan/60 group-hover:bg-cyan group-hover:text-ink transition-colors whitespace-nowrap">
              <Download className="w-3.5 h-3.5" /> Instalar App
            </span>
          </button>
          {user ? (
            <>
              <Link to="/panel" className="relative inline-flex items-center group transition-transform hover:scale-105">
                <span className="absolute inset-0 bg-cyan animate-ping opacity-30 pointer-events-none" style={{ animationDuration: "2s" }} />
                <span className="relative bg-cyan group-hover:bg-cyan-hover text-ink glow-cyan px-4 py-2 text-sm font-display font-bold uppercase tracking-wide transition-colors whitespace-nowrap">
                  Ir al Panel
                </span>
              </Link>
              <UserMenu />
            </>
          ) : (
            <>
              <Link to="/login" className={`px-4 py-2 text-sm font-medium ${scrolled || !isLandingHero ? "text-ink-text border-ink-border" : "text-white border-white/50"} hover:border-cyan hover:text-cyan transition-colors whitespace-nowrap shrink-0`}>
                Iniciar sesión
              </Link>
              <Link to="/register" className="px-4 py-2 text-sm font-display font-bold uppercase tracking-wide bg-cyan hover:bg-cyan-hover text-ink glow-cyan transition-colors whitespace-nowrap shrink-0">
                Crear cuenta
              </Link>
            </>
          )}
        </div>

        <button className={`md:hidden p-2 ${navColor}`} onClick={() => setOpen(v => !v)} aria-label="Menú">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile slide menu (compacto, estilo plataforma) */}
      {open && createPortal(
        <div className="md:hidden fixed inset-0 z-[60] bg-ink/85 backdrop-blur" role="dialog" aria-modal="true" aria-label="Menú de navegación" onClick={() => setOpen(false)}>
          <div className="absolute left-0 top-0 bottom-0 w-72 max-w-[82vw] bg-ink-surface border-r border-ink-border flex flex-col overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-4 border-b border-ink-border">
              <div className="flex items-center gap-2.5">
                <Image src={branding?.logo_url || LOGO_URL} alt={branding?.brand_name ? `${branding.brand_name} ${branding.tagline || ""}`.trim() : "BHGC Nexus Logistics"} className="w-8 h-8" fittingType="fill" />
                <div className="leading-tight">
                  <div className="font-display font-bold text-sm tracking-wide text-ink-text">{branding?.brand_name || "BHGC NEXUS"}</div>
                  <div className="text-[0.55rem] uppercase tracking-widest text-cyan">{branding?.tagline || "Logistics"}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink-text p-1"><X className="w-5 h-5" /></button>
            </div>

            <nav className="flex flex-col p-2">
              <Link to={user ? "/inicio" : "/"} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-ink-text hover:text-cyan hover:bg-cyan/5 transition-colors">
                <Home className="w-4 h-4" /> Inicio
              </Link>
              {NAV_LINKS.map(l => l.children ? (
                <div key={l.label}>
                  <div className="w-full flex items-center justify-between">
                    <Link to={l.to} onClick={() => setOpen(false)}
                      className="flex-1 px-3 py-2.5 text-sm font-medium text-ink-text hover:text-cyan hover:bg-cyan/5 transition-colors">
                      {l.label}
                    </Link>
                    <button
                      onClick={() => setOpenSub(openSub === l.label ? null : l.label)}
                      className="px-3 py-2.5 text-ink-muted hover:text-cyan hover:bg-cyan/5 transition-colors"
                      aria-label={`Expandir ${l.label}`}
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openSub === l.label ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {openSub === l.label && (
                    <div className="pl-3 border-l border-ink-border ml-3 my-1">
                      {l.mega ? (
                        <div className="max-h-80 overflow-y-auto pr-1 -mr-1">
                          {l.featured && l.featured.map(c => {
                            const FIcon = c.icon;
                            return (
                              <Link key={c.label} to={c.to} onClick={() => setOpen(false)}
                                className="flex items-start gap-2 px-2 py-2 hover:bg-cyan/5 transition-colors">
                                <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-cyan/40 text-cyan">
                                  {FIcon && <FIcon className="w-3.5 h-3.5" />}
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block text-xs font-medium text-ink-text truncate">{c.label}</span>
                                  <span className="block text-[0.6rem] text-ink-muted leading-tight line-clamp-2 break-words">{c.desc}</span>
                                </span>
                              </Link>
                            );
                          })}
                          <div className="border-t border-ink-border my-1.5"></div>
                          <div className="text-[0.6rem] uppercase tracking-widest text-cyan font-bold px-2 py-1">Legal y Política</div>
                          {l.children.map(g => {
                            const GIcon = g.icon;
                            return (
                              <div key={g.title} className="mb-1.5">
                                <div className="flex items-center gap-1.5 px-2 py-1.5">
                                  <GIcon className="w-3.5 h-3.5 text-cyan shrink-0" />
                                  <span className="text-[0.62rem] uppercase tracking-wide font-display font-bold text-ink-text">{g.title}</span>
                                </div>
                                {g.links.map(c => (
                                  <Link key={c.to} to={c.to} onClick={() => setOpen(false)}
                                    className="block pl-8 pr-2 py-1.5 text-[0.7rem] text-ink-muted hover:text-cyan hover:bg-cyan/5 transition-colors leading-snug">{c.t}</Link>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ) : l.grid ? (
                        l.children.map(c => {
                          const Icon = c.icon;
                          return (
                            <Link key={c.label} to={c.to} onClick={() => setOpen(false)}
                              className="flex items-start gap-2 px-3 py-2 hover:bg-cyan/5 transition-colors">
                              <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-cyan/40 text-cyan">
                                {Icon && <Icon className="w-3.5 h-3.5" />}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-xs font-medium text-ink-text truncate">{c.label}</span>
                                <span className="block text-[0.6rem] text-ink-muted leading-tight line-clamp-2 break-words">{c.desc}</span>
                              </span>
                            </Link>
                          );
                        })
                      ) : l.rich ? (
                        l.children.map(c => {
                          const Icon = c.icon;
                          return (
                            <Link key={c.label} to={c.to} onClick={() => setOpen(false)}
                              className="flex items-start gap-2 px-3 py-2 hover:bg-cyan/5 transition-colors">
                              <span className="shrink-0 w-7 h-7 flex items-center justify-center border border-cyan/40 text-cyan">
                                {Icon && <Icon className="w-3.5 h-3.5" />}
                              </span>
                              <span className="min-w-0">
                                <span className="block text-xs font-medium text-ink-text">{c.label}</span>
                                <span className="block text-[0.6rem] text-ink-muted leading-tight">{c.desc}</span>
                              </span>
                            </Link>
                          );
                        })
                      ) : l.wide ? (
                        <>
                          <div className="max-h-64 overflow-y-auto pr-1">
                            {l.children.filter(c => !c.all).map(c => (
                              <Link key={c.label} to={c.to} onClick={() => setOpen(false)}
                                className={`block px-3 py-1.5 text-xs hover:text-cyan transition-colors ${c.main ? "text-cyan font-medium" : "text-ink-muted"}`}>
                                {c.label}
                              </Link>
                            ))}
                          </div>
                          {l.children.filter(c => c.all).map(c => (
                            <Link key={c.label} to={c.to} onClick={() => setOpen(false)}
                              className="block px-3 py-2 mt-1 border-t border-ink-border text-xs text-cyan font-medium hover:bg-cyan/5 transition-colors">
                              {c.label}
                            </Link>
                          ))}
                        </>
                      ) : (
                        l.children.map(c => (
                          <Link key={c.label} to={c.to} onClick={(e) => { handleSectionClick(e, c.to); setOpen(false); }}
                            className={`block px-3 py-2 text-xs hover:text-cyan transition-colors ${c.all ? "text-cyan font-medium" : "text-ink-muted"}`}>
                            {c.label}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={l.to} to={resolvedTo(l)} onClick={(e) => handleSectionClick(e, resolvedTo(l))}
                  className="px-3 py-2.5 text-sm font-medium text-ink-text hover:text-cyan hover:bg-cyan/5 transition-colors">{l.label}</Link>
              ))}
            </nav>

            <div className="mt-auto p-3 border-t border-ink-border flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <NavSearchOverlay />
                <LanguageSelector compact />
                <SoundToggle />
                <ThemeToggle />
                <button onClick={() => { setDownloadOpen(true); setOpen(false); }}
                  className="relative flex-1 inline-flex items-center justify-center group transition-transform active:scale-95">
                  <span className="absolute inset-0 border border-cyan animate-ping opacity-40 pointer-events-none" style={{ animationDuration: "2.4s" }} />
                  <span className="relative flex items-center justify-center gap-1.5 px-3 py-2 w-full text-xs text-cyan border border-cyan/60 group-hover:bg-cyan group-hover:text-ink transition-colors">
                    <Download className="w-3.5 h-3.5" /> Instalar App
                  </span>
                </button>
              </div>
              {user ? (
                <Link to="/panel" onClick={() => setOpen(false)}
                  className="relative inline-flex items-center justify-center w-full group transition-transform active:scale-95">
                  <span className="absolute inset-0 bg-cyan animate-ping opacity-30 pointer-events-none" style={{ animationDuration: "2s" }} />
                  <span className="relative bg-cyan text-ink glow-cyan px-4 py-2.5 text-center text-sm font-display font-bold uppercase w-full transition-colors">
                    Ir al Panel
                  </span>
                </Link>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" onClick={() => setOpen(false)}
                    className="px-3 py-2.5 text-center text-xs border border-ink-border hover:border-cyan hover:text-cyan transition-colors">Iniciar sesión</Link>
                  <Link to="/register" onClick={() => setOpen(false)}
                    className="px-3 py-2.5 text-center text-xs font-display font-bold uppercase bg-cyan text-ink">Crear cuenta</Link>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <DownloadAppModal open={downloadOpen} onClose={() => setDownloadOpen(false)} />
    </header>
  );
}